import React, { useState, useEffect } from 'react';
import Icon, { ICONS } from '../icons';
import Tooltip from '../Tooltip';

interface ImageSizeValue {
  width: number;
  height: number;
}

interface AspectRatioConfig {
  ratio: string;
  width?: number | null;
  height?: number | null;
}

interface ImageSizeConfig {
  min_width: number;
  max_width: number;
  min_height: number;
  max_height: number;
  default_width: number;
  default_height: number;
  step: number;
  aspect_ratios?: (string | AspectRatioConfig)[];
  default_aspect_ratio?: string | null;
}

interface ImageSizeInputProps {
  value: ImageSizeValue | null;
  onChange: (value: ImageSizeValue) => void;
  config: ImageSizeConfig;
  className?: string;
}

export default function ImageSizeInput({ value, onChange, config, className = '' }: ImageSizeInputProps) {
  // Initialize with default values or provided value
  const [width, setWidth] = useState(value?.width || config.default_width || 768);
  const [height, setHeight] = useState(value?.height || config.default_height || 1024);
  const [widthText, setWidthText] = useState(String(value?.width || config.default_width || 768));
  const [heightText, setHeightText] = useState(String(value?.height || config.default_height || 1024));
  const [isLocked, setIsLocked] = useState(false);
  const [selectedRatio, setSelectedRatio] = useState<string>('Custom');
  
  // Aspect ratio constants for common ratios
  const aspectRatios: Record<string, number> = {
    '9:21': 9 / 21,
    '9:16': 9 / 16,
    '9:18': 9 / 18,
    '3:4': 3 / 4,
    '1:1': 1,
    '4:3': 4 / 3,
    '16:9': 16 / 9,
    '18:9': 18 / 9,
    '21:9': 21 / 9
  };
  
  // Calculate current aspect ratio
  const currentRatio = width / height;
  
  // Sync local text state when width/height change programmatically (slider, aspect ratio, swap)
  useEffect(() => {
    setWidthText(String(width));
  }, [width]);

  useEffect(() => {
    setHeightText(String(height));
  }, [height]);

  // Update parent component when dimensions change
  useEffect(() => {
    onChange({ width, height });
  }, [width, height]);
  
  // Handle width change
  const handleWidthChange = (newWidth: number) => {
    // Ensure width is within bounds and respects step
    const stepAlignedWidth = Math.round(newWidth / config.step) * config.step;
    const clampedWidth = Math.max(config.min_width, Math.min(config.max_width, stepAlignedWidth));

    setWidth(clampedWidth);

    if (isLocked) {
      // Maintain current aspect ratio
      const lockedRatio = width / height;
      const newHeight = clampedWidth / lockedRatio;
      const clampedHeight = Math.max(config.min_height, Math.min(config.max_height, newHeight));
      setHeight(Math.round(clampedHeight));
    } else {
      setSelectedRatio('Custom');
    }
  };
  
  // Handle height change
  const handleHeightChange = (newHeight: number) => {
    // Ensure height is within bounds and respects step
    const stepAlignedHeight = Math.round(newHeight / config.step) * config.step;
    const clampedHeight = Math.max(config.min_height, Math.min(config.max_height, stepAlignedHeight));

    setHeight(clampedHeight);

    if (isLocked) {
      // Maintain current aspect ratio
      const lockedRatio = width / height;
      const newWidth = clampedHeight * lockedRatio;
      const clampedWidth = Math.max(config.min_width, Math.min(config.max_width, newWidth));
      setWidth(Math.round(clampedWidth));
    } else {
      setSelectedRatio('Custom');
    }
  };
  
  // Handle aspect ratio selection
  const handleRatioSelect = (ratio: string) => {
    setSelectedRatio(ratio);
    if (ratio !== 'Custom') {
      // Find the ratio config from availableRatios
      const ratioConfig = availableRatios.find(r =>
        typeof r === 'object' ? r.ratio === ratio : r === ratio
      );

      // Check if we have a fixed size for this ratio
      if (typeof ratioConfig === 'object' && ratioConfig.width && ratioConfig.height) {
        // Use the fixed size
        const clampedWidth = Math.max(config.min_width, Math.min(config.max_width, ratioConfig.width));
        const clampedHeight = Math.max(config.min_height, Math.min(config.max_height, ratioConfig.height));
        setWidth(clampedWidth);
        setHeight(clampedHeight);
      } else if (aspectRatios[ratio]) {
        // No fixed size - calculate from aspect ratio
        const targetRatio = aspectRatios[ratio];
        const newHeight = width / targetRatio;
        const clampedHeight = Math.max(config.min_height, Math.min(config.max_height, newHeight));
        setHeight(Math.round(clampedHeight));
      }
    }
  };
  
  // Swap width and height
  const handleSwap = () => {
    const tempWidth = width;
    const tempHeight = height;

    // Check if swapped values are within bounds
    const newWidth = Math.max(config.min_width, Math.min(config.max_width, tempHeight));
    const newHeight = Math.max(config.min_height, Math.min(config.max_height, tempWidth));

    setWidth(newWidth);
    setHeight(newHeight);

    // If a preset was selected, try to find matching preset for swapped dimensions
    if (selectedRatio !== 'Custom') {
      const newRatio = newWidth / newHeight;
      let matchedRatio: string | null = null;

      // Search for a matching preset
      for (const [name, ratio] of Object.entries(aspectRatios)) {
        if (Math.abs(newRatio - ratio) < 0.02) {
          matchedRatio = name;
          break;
        }
      }

      if (matchedRatio) {
        setSelectedRatio(matchedRatio);
      } else {
        // No matching preset found, switch to Custom
        setSelectedRatio('Custom');
      }
    }
  };

  // Handle blur on width text input — clamp and commit
  const handleWidthBlur = () => {
    const parsed = parseInt(widthText);
    if (isNaN(parsed) || widthText.trim() === '') {
      // Reset to current width
      setWidthText(String(width));
    } else {
      handleWidthChange(parsed);
    }
  };

  // Handle blur on height text input — clamp and commit
  const handleHeightBlur = () => {
    const parsed = parseInt(heightText);
    if (isNaN(parsed) || heightText.trim() === '') {
      setHeightText(String(height));
    } else {
      handleHeightChange(parsed);
    }
  };

  // Toggle lock
  const toggleLock = () => {
    setIsLocked(!isLocked);
  };

  const availableRatios = config.aspect_ratios || ['9:21', '9:18', '9:16', '3:4', '1:1', '4:3', '16:9', '18:9', '21:9'];

  // Helper to get ratio string from config
  const getRatioString = (ratioConfig: string | AspectRatioConfig): string => {
    return typeof ratioConfig === 'string' ? ratioConfig : ratioConfig.ratio;
  };

  // Helper to get display label for ratio
  const getRatioLabel = (ratioConfig: string | AspectRatioConfig): string => {
    if (typeof ratioConfig === 'string') return ratioConfig;
    const { ratio, width, height } = ratioConfig;
    if (width && height) {
      return `${ratio} (${width}×${height})`;
    }
    return ratio;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Aspect Ratio Selector */}
      <div>
        <div className="flex items-center gap-2">
          <select
            value={selectedRatio}
            onChange={(e) => handleRatioSelect(e.target.value)}
            className="flex-1 px-3 py-2 bg-gray-700 text-white text-sm rounded-lg border border-gray-600 hover:border-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors"
          >
            <option value="Custom">Custom</option>
            {availableRatios.map((ratioConfig) => {
              const ratioValue = getRatioString(ratioConfig);
              const label = getRatioLabel(ratioConfig);
              return (
                <option key={ratioValue} value={ratioValue}>
                  {label}
                </option>
              );
            })}
          </select>

          {/* Lock Button */}
          <Tooltip content={isLocked ? "Unlock aspect ratio" : "Lock aspect ratio"}>
            <button
              type="button"
              onClick={toggleLock}
              className={`h-[38px] px-3 rounded-lg transition-all flex items-center justify-center ${
                isLocked
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white'
              }`}
            >
              <Icon name={isLocked ? ICONS.LOCK : ICONS.UNLOCK} size={16} />
            </button>
          </Tooltip>

          {/* Swap Button */}
          <Tooltip content="Swap width and height">
            <button
              type="button"
              onClick={handleSwap}
              className="h-[38px] px-3 bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white rounded-lg transition-all flex items-center justify-center"
            >
              <Icon name={ICONS.SWAP} size={16} />
            </button>
          </Tooltip>
        </div>
      </div>
      
      {/* Width Control */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-300">Width</label>
          <input
            type="number"
            value={widthText}
            min={config.min_width}
            max={config.max_width}
            step={config.step}
            onChange={(e) => setWidthText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
            onBlur={handleWidthBlur}
            className="w-16 text-xs text-white font-mono text-right bg-transparent border border-gray-600 rounded px-1 py-0.5 focus:border-blue-500 focus:outline-none"
          />
        </div>
        <input
          type="range"
          min={config.min_width}
          max={config.max_width}
          step={config.step}
          value={width}
          onChange={(e) => handleWidthChange(parseInt(e.target.value))}
          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
        />
      </div>

      {/* Height Control */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-medium text-gray-300">Height</label>
          <input
            type="number"
            value={heightText}
            min={config.min_height}
            max={config.max_height}
            step={config.step}
            onChange={(e) => setHeightText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
            onBlur={handleHeightBlur}
            className="w-16 text-xs text-white font-mono text-right bg-transparent border border-gray-600 rounded px-1 py-0.5 focus:border-blue-500 focus:outline-none"
          />
        </div>
        <input
          type="range"
          min={config.min_height}
          max={config.max_height}
          step={config.step}
          value={height}
          onChange={(e) => handleHeightChange(parseInt(e.target.value))}
          className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
        />
      </div>
      
      {/* Current Ratio Display */}
      <div className="flex items-center justify-center">
        <div className="text-xs text-gray-400 font-mono">
        </div>
      </div>
    </div>
  );
}